/**
 * #1184 · El cálculo de la calculadora de tarifas, sin pantalla: los cinco modos con los que
 * calculan las herramientas de `tarifa_herramientas`.
 *
 *  - **fórmula**: `formula.tpc` / `formula.tpi`, las cadenas de la herramienta (casi todas);
 *  - **manual** (SMEX): los minutos los teclea la persona;
 *  - **tabla de precios** (AE Ascensores): precio por tramo de unidades;
 *  - **clasificación** (AP): PS × V decide la categoría y no hay precio;
 *  - **RITE**: delega en `rite-motor.js`, que es el cálculo aprobado con el Excel de Dirección.
 *
 * Vivía dentro de `tarifa-calculador-v3.vue`, repartido por `calc()` y siete métodos auxiliares
 * que leían `this.form` y `this.CFG`. Así no se podía calcular nada sin montar la pantalla, y eso es
 * justo lo que necesitan los casos de prueba por herramienta (#1185) y la comparación antes/después
 * de un cambio de tarifa (#1188). Es una EXTRACCIÓN: ni una operación ha cambiado de orden —en
 * coma flotante el orden de una suma puede mover el último céntimo, y estos importes entran en la
 * oferta—, y el banco de cifras congeladas lo acredita contra lo que calculaba `develop`:
 *
 *   node scripts/prueba-calculador-modos-intactos.js
 *
 * Lo que entra es lo que tenía la pantalla delante: la herramienta, la inspección elegida (`srv`),
 * lo tecleado (`form`) y la configuración global (`cfg`). Lo que sale es lo que la pantalla
 * guardaba en `res`, `desglose`, `alertas` y `suggManut`, con la misma forma campo a campo: la
 * plantilla, el PDF y «Guardar en línea» lo leen de ahí y no se han tocado.
 *
 * Nada de aquí escribe en la entrada ni pide nada a la red.
 */
import RiteMotor from './rite-motor.js';
import ExpresionCondicion from './expresion-condicion.js';

// ─── Formatos ────────────────────────────────────────────────────────────────────────────────
// Viven aquí porque `res` trae cifras ya formateadas («1.072,89 €», «4h 28min»): si la pantalla
// formateara con otra copia, el importe pintado y el del cálculo podrían escribirse distinto.

/** Un importe con dos decimales y separadores españoles, sin unidad. */
function fN(n) {
  return (Math.round(n * 100) / 100).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtEur(n) { return fN(n) + ' €'; }

/** Minutos como horas y minutos: «4h 28min», «2h», «39min». */
function fH(m) {
  const h = Math.floor(m / 60), r = Math.round(m % 60);
  if (h) return r ? h + 'h ' + r + 'min' : h + 'h';
  return r ? r + 'min' : '0min';
}

// ─── Lo que la pantalla resolvía con sus `computed` ──────────────────────────────────────────

/**
 * Los parámetros con los que calcula la inspección `srv`: la `config` de la herramienta con los
 * `params` del tipo de inspección encima. Es lo que las fórmulas y las alertas ven como `params`.
 */
function parametrosDe(h, srv) {
  const cfg = (h && h.config) ? Object.assign({}, h.config) : {};
  const opciones = (h && h.subtipos && h.subtipos.opciones) || {};
  const m = opciones[srv];
  if (m && m.params) Object.assign(cfg, m.params);
  return cfg;
}

/**
 * Los kilómetros mínimos que de verdad se cobran: el de la herramienta si lo tiene —también si es
 * 0, que es un número—, y si no el global (#379). La pantalla los cita en la nota, en la hoja
 * impresa y en la línea de oferta, y los lee de aquí para no anunciar un mínimo y cobrar otro.
 */
function kmMinimo(h, cfg) {
  const cfgH = h && h.config;
  return (cfgH && typeof cfgH.kmmin === 'number') ? cfgH.kmmin : (cfg || {}).kmmin;
}

/**
 * Familia (`calor` | `frio` | `mixta`) y modo (`per` | `ini`) de una inspección de RITE. Los
 * declara el subtipo (`params.fam`/`params.mod`); el respaldo por el nombre de la clave está para
 * las líneas guardadas con la versión anterior, que traen `srv` pero no su definición. Lo usan el
 * cálculo y la matriz del diseño nuevo, para que no puedan discrepar.
 */
function familiaYModo(clave, params) {
  const p = params || {};
  const c = String(clave || '');
  let fam = 'calor';
  if (c.indexOf('frio') === 0) fam = 'frio';
  else if (c.indexOf('mixto') === 0) fam = 'mixta';
  return { fam: p.fam || fam, mod: p.mod || (c.slice(-2) === '_i' ? 'ini' : 'per') };
}

// ─── Piezas comunes a varios modos ───────────────────────────────────────────────────────────

/**
 * Los gastos de una visita: el detalle que pinta el panel y el total que entra en el precio.
 *
 * `suf` es `''` para la 1ª y `'2'` para la 2ª, que es como están nombrados los campos del
 * formulario (`ncom` / `ncom2`). El total se suma **en el mismo orden** que antes (subcontratación,
 * peajes, comidas, cenas, hotel, desayunos) y no recorriendo la lista del desglose, que va en otro
 * orden: en coma flotante el orden de la suma puede mover el último céntimo.
 */
function gastosDeVisita(form, cfg, suf) {
  const f = form, c = cfg;
  const s = suf || '';
  const dosa = s ? ' 2ª' : '';
  return {
    lineas: [
      { k: 'Subcontrat.' + dosa, v: f['sub' + s] || 0 },
      { k: 'Comidas' + dosa, v: (f['ncom' + s] || 0) * c.com },
      { k: 'Cenas' + dosa, v: (f['ncen' + s] || 0) * c.cen },
      { k: 'Hotel' + dosa, v: (f['nhot' + s] || 0) * c.hot },
      { k: 'Desayunos' + dosa, v: (f['ndes' + s] || 0) * c.des },
      { k: 'Peajes' + dosa, v: f['peaj' + s] || 0 }
    ],
    total: (f['sub' + s] || 0) + (f['peaj' + s] || 0) + (f['ncom' + s] || 0) * c.com
      + (f['ncen' + s] || 0) * c.cen + (f['nhot' + s] || 0) * c.hot + (f['ndes' + s] || 0) * c.des
  };
}

/** El TPI como porcentaje del TPC: el de la herramienta si lo declara, y si no el medido. */
function tpiPorcentaje(h, tpc, tpi) {
  if (h.config && h.config.tpi_pct) return h.config.tpi_pct * 100;
  return (tpc > 0 && tpi > 0) ? Math.round(tpi / tpc * 100) : 0;
}

/** La sugerencia de dietas por duración de la jornada, igual para todas las herramientas. */
function sugerenciaManutencion(form, horas) {
  if (horas > 8 && !form.nhot) return '💡 >8h: cena+hotel+desayuno';
  if (horas > 5 && !form.ncom) return '💡 >5h: considera comida';
  return '';
}

/**
 * Las alertas de la herramienta que se cumplen con lo tecleado. Las condiciones son cadenas
 * guardadas en Mongo y se calculan con el intérprete de `expresion-condicion.js`, no con
 * `new Function`: ejecutarlas era ejecución de código arbitrario en el navegador de quien abriera
 * el calculador (#466). Una condición que no evalúe no saca su alerta y no deja sin precio.
 */
function evaluarAlertas(h, form, params) {
  const als = [];
  if (h && h.alertas) {
    h.alertas.forEach(function (a) {
      if (ExpresionCondicion.cumple(a.condicion, { form: form, params: params }, false)) als.push(a.mensaje);
    });
  }
  return als;
}

// ─── RITE ────────────────────────────────────────────────────────────────────────────────────

/**
 * El formulario de la pantalla, en la entrada que espera `rite-motor.js` (#466).
 *
 * Dos traducciones que no son evidentes y que, mal hechas, cambian el importe:
 *
 *  - **El descuento.** En la pantalla `form.dto` es un MULTIPLICADOR (1 = sin descuento, 0,9 =
 *    un 10%) y el motor lo quiere en tanto por ciento, porque así está en el Excel y en la
 *    maqueta. El `|| 1` reproduce el de la rama de fórmula: un 0 tecleado se lee como «sin
 *    descuento», no como «gratis». Y el porcentaje se redondea a dos decimales a propósito:
 *    `(1 - 0,9) × 100` da 10,000000000000009 en coma flotante, y el motor volvería a dividir
 *    por 100 dejando un descuento que no es el que se ha pedido.
 *  - **`fam` y `mod`**, que son los que deciden cuál de las seis inspecciones se calcula: ver
 *    `familiaYModo`.
 */
function estadoRite(h, form, srv, params) {
  const f = form || {};
  const fm = familiaYModo(f.srv || srv, params);
  const pct = function (v) { return Math.round((1 - (v || 1)) * 10000) / 100; };
  const S = {
    fam: fm.fam, mod: fm.mod,
    sup: f.sup || 0, zon: f.zon || 0, uta: f.uta || 0, emi: f.emi || 0,
    kw: f.kw || 0, ngc: f.ngc || 0,
    km: f.km || 0, mismo: !!f.mismo, fs: !!f.recargoFS, noc: !!f.recargoNocturno,
    dto: pct(f.dto), dto2: pct(f.dto2),
    g: { sub: f.sub || 0, peaj: f.peaj || 0, ncom: f.ncom || 0, ncen: f.ncen || 0, nhot: f.nhot || 0, ndes: f.ndes || 0 },
    g2: { sub: f.sub2 || 0, peaj: f.peaj2 || 0, ncom: f.ncom2 || 0, ncen: f.ncen2 || 0, nhot: f.nhot2 || 0, ndes: f.ndes2 || 0 }
  };
  // Los equipos de frío por rango: las claves son las de la tabla de bandas de la herramienta,
  // no una lista escrita aquí, para que añadir o quitar un rango sea un cambio de configuración.
  RiteMotor.parametros(h && h.config && h.config.rite)
    .BANDS.forEach(function (b) { S[b.k] = f[b.k] || 0; });
  return S;
}

/** El desglose de gastos del motor, en los `{k, v}` que pinta el panel del resultado. */
function gastosRite(p) {
  return (p.gs || []).map(function (g) { return { k: g[0], v: g[1] }; });
}

/**
 * Modo RITE (Climatización: el Excel de frío de Dirección, 09/10/2026). El cálculo vive en
 * `rite-motor.js`, contra el que corren las 255 pruebas de la maqueta aprobada
 * (`node scripts/prueba-rite-motor.js`); aquí sólo se traduce la entrada y la salida.
 */
function calcRite(h, form, srv, params, c, ph, kmin) {
  const PAR = RiteMotor.parametros(h.config && h.config.rite);
  const x = RiteMotor.calc(estadoRite(h, form, srv, params), {
    ph: ph, kmmin: kmin, tdf: c.tdf, kmcost: c.kmcost,
    com: c.com, cen: c.cen, hot: c.hot, des: c.des,
    // El motor llama `pctNoc` a lo que la configuración global llama `pctNocturno`.
    pctFS: c.pctFS, pctNoc: c.pctNocturno
  }, PAR);
  const pct = x.p1.total > 0 ? Math.round(x.p2.total / x.p1.total * 100) : 0;
  return {
    alertas: evaluarAlertas(h, form, params),
    suggManut: sugerenciaManutencion(form, x.U / 60),
    desglose: null,
    res: {
      tpc: Math.round(x.tpc), tpi: Math.round(x.tpi), td: Math.round(x.td), ttot: Math.round(x.U),
      // El TPI de RITE es siempre el MEDIDO sobre el campo, no el `tpi_pct` de la herramienta: en
      // frío son los 30 min de la instalación y en calor el 12%, así que el porcentaje sale de la
      // división y no de una constante.
      horas: fH(x.U), tpce: (x.tpc > 0 && x.tpi > 0 ? Math.round(x.tpi / x.tpc * 100) : 0) + '%',
      p1Fmt: fmtEur(x.p1.total), p2Fmt: fmtEur(x.p2.total),
      pct: x.p1.total > 0 ? pct + '%' : '—',
      p1trab: fmtEur(x.p1.trab), p2trab: fmtEur(x.p2.trab),
      tpc2Label: Math.round(x.tpc2) + ' / ' + Math.round(x.tpi2) + ' min',
      kmLabel: x.km + ' km',
      rFS1: x.p1.rfs, rFS2: x.p2.rfs, rN1: x.p1.rn, rN2: x.p2.rn,
      gastos1: gastosRite(x.p1), gastos2: gastosRite(x.p2),
      _p1: x.p1.total, _p2: x.p2.total,
      tpc2: Math.round(x.tpc2), tpi2: Math.round(x.tpi2), pct2a: pct,
      // Lo que añade RITE: de dónde sale cada minuto, qué datos faltan y la visita fallida.
      why1: x.why1, miss: x.miss, fail: x.fail, failD: x.failD,
      // Lo que pinta el panel del diseño nuevo (#467): el desglose de cada visita tal cual lo da el
      // motor, los km cobrados y cuántos generadores de frío ha contado para «Estado de los datos».
      p1: x.p1, p2: x.p2, km: x.km, nFrio: x.nFrio
    }
  };
}

// ─── Clasificación y tabla de precios ────────────────────────────────────────────────────────

/** Modo CLASIFICACIÓN (AP Clasificación): sólo la categoría que corresponde a PS × V. */
function calcClasificacion(h, form) {
  const ps = form.ps || 0, vol = form.vol || 0;
  const psxv = Math.round(ps * vol * 100) / 100;
  const cats = h.config.clasificacion.categorias || [];
  let catFound = null;
  for (let ci = 0; ci < cats.length; ci++) {
    if (psxv >= cats[ci].min && psxv <= cats[ci].max) { catFound = cats[ci]; break; }
  }
  return {
    alertas: [], suggManut: '', desglose: null,
    res: { tipo: 'clasificacion', psxv: psxv, categoria: catFound, sinOCA: psxv > 0 && psxv < 51 }
  };
}

/**
 * Modo TABLA DE PRECIOS (AE Ascensores). Los minutos son fijos por unidad (55 de campo y 5 de
 * oficina) y no hay alertas: así calculaba la pantalla y así se queda.
 */
function calcTablaPrecio(h, form, srv, c, kmin) {
  const tabla = h.config.tablaPrecios;
  const tipoInsp = srv || 'periodica';
  const n = form.n || 1;
  let tranche = '99';
  if (n <= 1) tranche = '1';
  else if (n <= 2) tranche = '2';
  else if (n <= 6) tranche = '6';
  const tabla1 = tabla[tipoInsp] || {};
  const tabla2 = tabla[tipoInsp + '2'] || {};
  const pu1 = tabla1[tranche] || 0;
  const pu2 = tabla2[tranche] || 0;
  const tpcAE = n * 55, tpiAE = n * 5;
  const kmAE = form.mismo ? 0 : Math.max(form.km || 0, kmin);
  const tdAE = form.mismo ? 0 : Math.round(kmAE * c.tdf * 100) / 100;
  const dtoAE = form.dto || 1, dto2AE = form.dto2 || 1;
  const kmcostAE = kmAE * c.kmcost;
  const gastosAE1 = gastosDeVisita(form, c, '');
  const gastosAE2 = gastosDeVisita(form, c, '2');
  const w1AE = n * pu1 * dtoAE;
  const b1AE = w1AE + kmcostAE;
  const rFS1AE = form.recargoFS ? Math.round(b1AE * (c.pctFS / 100) * 100) / 100 : 0;
  const rN1AE = form.recargoNocturno ? Math.round(b1AE * (c.pctNocturno / 100) * 100) / 100 : 0;
  const p1AE = b1AE + rFS1AE + rN1AE + gastosAE1.total;
  const w2AE = n * pu2 * dto2AE;
  const b2AE = w2AE + kmcostAE;
  const rFS2AE = form.recargoFS ? Math.round(b2AE * (c.pctFS / 100) * 100) / 100 : 0;
  const rN2AE = form.recargoNocturno ? Math.round(b2AE * (c.pctNocturno / 100) * 100) / 100 : 0;
  const p2AE = b2AE + rFS2AE + rN2AE + gastosAE2.total;
  const UAe = tpcAE + tpiAE + tdAE;
  return {
    alertas: [], suggManut: sugerenciaManutencion(form, UAe / 60), desglose: null,
    res: {
      tpc: Math.round(tpcAE), tpi: Math.round(tpiAE), td: Math.round(tdAE), ttot: Math.round(UAe),
      horas: fH(UAe), tpce: '—',
      p1Fmt: fmtEur(p1AE), p2Fmt: fmtEur(p2AE),
      pct: p1AE > 0 ? Math.round(p2AE / p1AE * 100) + '%' : '—',
      p1trab: fmtEur(w1AE) + ' (' + n + '×' + fN(pu1) + ')', p2trab: fmtEur(w2AE) + ' (' + n + '×' + fN(pu2) + ')',
      tpc2Label: '—', kmLabel: kmAE + ' km',
      rFS1: rFS1AE, rFS2: rFS2AE, rN1: rN1AE, rN2: rN2AE,
      gastos1: gastosAE1.lineas, gastos2: gastosAE2.lineas, _p1: p1AE, _p2: p2AE
    }
  };
}

// ─── Fórmula y manual ────────────────────────────────────────────────────────────────────────

/**
 * El TPC y el TPI de una herramienta de fórmula: las cadenas `formula.tpc` / `formula.tpi` que
 * guarda la herramienta, ejecutadas con `new Function`.
 *
 * **Se ha movido tal cual, con su defecto y su riesgo, a propósito.** Una fórmula que lanza —mal
 * escrita, o que lee un campo que no existe— deja 0 minutos sin decir nada, y la pantalla sigue
 * dando un precio de sólo desplazamiento. Y ejecutar como código lo que guarda la herramienta no es
 * seguro: es el riesgo que ya tenía la pantalla, reconocido en Sonar (S1523) y no resuelto. Cambiar
 * cualquiera de las dos cosas no es de esta tarea (#1184); la #1193 lo propone, aplazada. El banco lo
 * congela (`formula · formula que revienta…`, `…mal escrita…`, `solo el TPI revienta…`) para que se vea.
 */
function minutosDeFormula(h, form, params) {
  let tpc = 0, tpi = 0;
  if (h.formula && h.formula.tpc) {
    try { tpc = new Function('form', 'params', 'return ' + h.formula.tpc)(form, params); } catch (e) { tpc = 0; }
  }
  tpc = Math.round((tpc || 0) * 100) / 100;
  if (h.formula && h.formula.tpi) {
    try { tpi = new Function('form', 'params', 'tpc', 'return ' + h.formula.tpi)(form, params, tpc); } catch (e) { tpi = 0; }
  }
  tpi = Math.round((tpi || 0) * 100) / 100;
  return { tpc: tpc, tpi: tpi };
}

/**
 * Modos FÓRMULA y MANUAL (SMEX): los dos dan TPC y TPI, y de ahí en adelante el precio es el mismo
 * cálculo. También es por donde caen una tabla de precios o una clasificación sin su tabla.
 */
function calcFormula(h, form, params, c, ph, kmin, modo) {
  let tpc = 0, tpi = 0;
  if (modo === 'manual') {
    tpc = form.tpc || 0;
    tpi = form.tpi || 0;
  } else {
    const m = minutosDeFormula(h, form, params);
    tpc = m.tpc;
    tpi = m.tpi;
  }

  // ── Desplazamiento ──
  const km = form.mismo ? 0 : Math.max(form.km || 0, kmin);
  const td = form.mismo ? 0 : Math.round(km * c.tdf * 100) / 100;
  const dto = form.dto || 1, dto2 = form.dto2 || 1;

  // ── Tiempos ──
  const U = tpc + tpi + td;
  const horas = U / 60;
  const kmcost = km * c.kmcost;

  // ── 1ª Inspección ──
  const w1 = (U / 60 * ph) * dto;
  const b1 = w1 + kmcost;
  const rFS1 = form.recargoFS ? Math.round(b1 * (c.pctFS / 100) * 100) / 100 : 0;
  const rN1 = form.recargoNocturno ? Math.round(b1 * (c.pctNocturno / 100) * 100) / 100 : 0;
  const gastosDe1a = gastosDeVisita(form, c, '');
  const gastosDe2a = gastosDeVisita(form, c, '2');
  const p1 = b1 + rFS1 + rN1 + gastosDe1a.total;

  // ── 2ª Inspección ──
  const f2tpc = params.f2tpc || (h.config && h.config.f2tpc) || 0.4;
  const f2tpi = params.f2tpi || (h.config && h.config.f2tpi) || 0.4;
  const tpc2 = tpc * f2tpc, tpi2 = tpi * f2tpi;
  const U2 = tpc2 + tpi2 + td;
  const w2 = (U2 / 60 * ph) * dto2;
  const b2 = w2 + kmcost;
  const rFS2 = form.recargoFS ? Math.round(b2 * (c.pctFS / 100) * 100) / 100 : 0;
  const rN2 = form.recargoNocturno ? Math.round(b2 * (c.pctNocturno / 100) * 100) / 100 : 0;
  const p2 = b2 + rFS2 + rN2 + gastosDe2a.total;

  const tpiPct = tpiPorcentaje(h, tpc, tpi);
  return {
    alertas: evaluarAlertas(h, form, params),
    suggManut: sugerenciaManutencion(form, horas),
    // Las cifras sin redondear que pinta el panel del diseño nuevo (#961): `res` sólo las trae ya
    // formateadas, y van aparte para que `res` siga siendo el de antes, campo a campo.
    desglose: { tpc: tpc, tpi: tpi, td: td, km: km, kmcost: kmcost, U: U, U2: U2, w1: w1, w2: w2, f2tpc: f2tpc, f2tpi: f2tpi },
    res: {
      tpc: Math.round(tpc), tpi: Math.round(tpi), td: Math.round(td), ttot: Math.round(U),
      horas: fH(U), tpce: tpiPct + '%',
      p1Fmt: fmtEur(p1), p2Fmt: fmtEur(p2),
      pct: p1 > 0 ? Math.round(p2 / p1 * 100) + '%' : '—',
      p1trab: fmtEur(w1), p2trab: fmtEur(w2),
      tpc2Label: Math.round(tpc2) + ' / ' + Math.round(tpi2) + ' min (' + Math.round(f2tpc * 100) + '%/' + Math.round(f2tpi * 100) + '%)',
      kmLabel: km + ' km',
      rFS1: rFS1, rFS2: rFS2, rN1: rN1, rN2: rN2,
      gastos1: gastosDe1a.lineas, gastos2: gastosDe2a.lineas,
      _p1: p1, _p2: p2,
      tpc2: Math.round(tpc2), tpi2: Math.round(tpi2),
      pct2a: p1 > 0 ? Math.round(p2 / p1 * 100) : 0
    }
  };
}

// ─── La entrada ──────────────────────────────────────────────────────────────────────────────

/**
 * Calcula una herramienta.
 *
 * `entrada`:
 *  - `herramienta`: la definición de `tarifa_herramientas` (la versión con la que se calcula);
 *  - `form`: lo tecleado, con `srv`, `km`, `dto`/`dto2`, gastos y recargos;
 *  - `srv`: la inspección elegida, si `form.srv` no la trae. Si los dos la traen manda `form.srv`;
 *  - `cfg`: la configuración global (`ph`, `kmcost`, `kmmin`, `tdf`, dietas y recargos);
 *  - `params` y `kmmin`: opcionales; si no vienen se resuelven como la pantalla
 *    (`parametrosDe`, `kmMinimo`).
 *
 * Devuelve `{ modo, res, desglose, alertas, suggManut }`, o `null` sin herramienta. `modo` es la
 * rama que ha calculado de verdad: una tabla de precios sin tabla dice `formula`. `desglose` sólo
 * lo dan fórmula y manual; en los demás modos es `null`.
 */
function calcular(entrada) {
  const e = entrada || {};
  const h = e.herramienta;
  if (!h) return null;
  // La inspección se resuelve UNA vez y la ven igual todas las ramas: los `params` del tipo, la
  // tabla de AE, la familia de RITE y las fórmulas y alertas, que la leen en `form.srv`. Antes cada
  // una la buscaba en un sitio —`srv`, `form.srv` o los dos—, y quien pasara sólo uno de los dos
  // calculaba con los parámetros de una inspección y la tabla de otra. `form.srv` manda porque es
  // lo que leía la pantalla en tabla y RITE; si no lo trae, se calcula sobre una copia que sí, sin
  // tocar la entrada. La pantalla pone los dos a la vez y además pasa sus `params`: para ella no
  // cambia nada, y el banco lo acredita contra las cifras de `develop`.
  const srv = (e.form && e.form.srv) || e.srv || null;
  const form = (!srv || (e.form && e.form.srv)) ? (e.form || {}) : Object.assign({}, e.form, { srv: srv });
  const c = e.cfg;
  const params = e.params || parametrosDe(h, srv);
  const kmin = e.kmmin !== undefined ? e.kmmin : kmMinimo(h, c);
  const modo = (h.config && h.config.modoCalculo) || 'formula';
  // Un solo precio hora para todas las herramientas: Dirección retiró el caso especial de RITE (#379).
  const ph = c.ph;
  let r;
  let rama = modo;
  if (modo === 'clasificacion' && h.config && h.config.clasificacion) {
    r = calcClasificacion(h, form);
  } else if (modo === 'tablaPrecio' && h.config && h.config.tablaPrecios) {
    r = calcTablaPrecio(h, form, srv, c, kmin);
  } else if (modo === 'rite') {
    r = calcRite(h, form, srv, params, c, ph, kmin);
  } else {
    rama = modo === 'manual' ? 'manual' : 'formula';
    r = calcFormula(h, form, params, c, ph, kmin, modo);
  }
  return { modo: rama, res: r.res, desglose: r.desglose, alertas: r.alertas, suggManut: r.suggManut };
}

const TarifaMotor = {
  calcular: calcular,
  parametrosDe: parametrosDe, kmMinimo: kmMinimo, familiaYModo: familiaYModo,
  gastosDeVisita: gastosDeVisita, evaluarAlertas: evaluarAlertas, estadoRite: estadoRite,
  fN: fN, fmtEur: fmtEur, fH: fH
};

export default TarifaMotor;
export {
  calcular, parametrosDe, kmMinimo, familiaYModo, gastosDeVisita, evaluarAlertas, estadoRite,
  fN, fmtEur, fH
};
